"use client";
import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { DealOfferEditionContext } from "@/components/marketplace/deal-offer-edition-context";
import type {
  DealOfferExplorerWorkRelease,
  OfferExplorerPayload,
} from "@/components/marketplace/deal-offer-explorer-types";
import { DealOfferFormatTabs } from "@/components/marketplace/deal-offer-format-tabs";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { getDealOfferExplorerFormatOptions } from "@/components/marketplace/get-deal-offer-explorer-format-options";
import { OfferListHeader } from "@/components/marketplace/offer-list-header";
import { sortOffersByTotal, summarizeOfferList } from "@/components/marketplace/offer-list-summary";
import { OfferRow, type OfferRowProps } from "@/components/marketplace/offer-row";
import { selectReleaseOffers } from "@/components/marketplace/selectReleaseOffers";
import { useOfferMediaPacking } from "@/components/marketplace/useOfferMediaPacking";
import type { FormatCategoryWithOther } from "@garimpa/lib/formatGroups";

/** Both work and release explorers describe the matched edition's full packing. */
function ExplorerOfferRow({
  releases,
  ...props
}: OfferRowProps & {
  releases: DealOfferExplorerWorkRelease[];
}) {
  const release = releases.find((item) => props.offer.matchedReleaseIds.includes(item.id));
  const packing = useOfferMediaPacking(
    props.offer.releaseMediaFormatSummary?.length
      ? props.offer.releaseMediaFormatSummary
      : release?.mediaFormatSummary,
    props.offer.formatCategory,
  );
  return <OfferRow {...props} listingDetails={packing} />;
}

function siblingReleases(
  offer: DealOffer,
  releases: DealOfferExplorerWorkRelease[],
  contextReleaseId?: string,
) {
  if (!contextReleaseId) return [];
  const releaseById = new Map(releases.map((release) => [String(release.id), release]));
  return offer.matchedReleaseIds.flatMap((matchedReleaseId) => {
    if (String(matchedReleaseId) === contextReleaseId) return [];
    const release = releaseById.get(String(matchedReleaseId));
    return release ? [release] : [];
  });
}

function ReleaseOfferExplorerSection({
  heading,
  offers,
  contextReleaseId,
  releases,
  currency,
  cheapestOfferId,
}: {
  heading: string;
  offers: DealOffer[];
  contextReleaseId: string;
  releases: DealOfferExplorerWorkRelease[];
  currency: string;
  cheapestOfferId: string | null;
}) {
  const t = useTranslations("shared.affiliates");
  const summary = summarizeOfferList(
    offers.map((offer) => ({ ...offer, currentPriceTotal: offer.currentPrice })),
  );
  return (
    <section className="@container min-w-0">
      <h3 className="px-4 pt-4 text-xs font-bold uppercase tracking-wider sm:px-5">{heading}</h3>
      {offers.length > 0 ? (
        <>
          <OfferListHeader
            summary={summary}
            condition={offers[0]?.currentCondition === "used" ? "used" : "new"}
          />
          <ul>
            {offers.map((offer) => {
              const siblings = siblingReleases(offer, releases, contextReleaseId);
              return (
                <ExplorerOfferRow
                  key={offer.offerId}
                  releases={releases}
                  offer={offer}
                  currency={currency}
                  cheapest={offer.offerId === cheapestOfferId}
                  contextReleaseId={contextReleaseId}
                  editionContext={
                    siblings.length > 0 ? (
                      <DealOfferEditionContext releases={siblings} />
                    ) : undefined
                  }
                />
              );
            })}
          </ul>
        </>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground sm:px-5">
          {t("catalogNoOffers", { entityType: "release" })}
        </p>
      )}
    </section>
  );
}

export function DealOfferExplorerBody({
  payload,
  offers,
  contextReleaseId,
  releases,
  currency,
}: {
  payload: OfferExplorerPayload;
  offers: DealOffer[];
  contextReleaseId?: string;
  releases: DealOfferExplorerWorkRelease[];
  currency: string;
}) {
  const t = useTranslations("shared.affiliates");
  const initialFormat = payload.format ?? payload.offer.formatCategory;
  const firstFormat =
    initialFormat ?? offers.find((offer) => offer.formatCategory)?.formatCategory ?? "other";
  const [activeFormat, setActiveFormat] = useState<FormatCategoryWithOther>(firstFormat);
  const formatOptions = useMemo(() => getDealOfferExplorerFormatOptions(offers), [offers]);
  const visibleOffers = useMemo(
    () => sortOffersByTotal(offers.filter((offer) => offer.formatCategory === activeFormat)),
    [activeFormat, offers],
  );
  const orderedOffers = useMemo(() => {
    const initiallySelected = visibleOffers.find(
      (offer) => offer.offerId === payload.offer.offerId,
    );
    if (!initiallySelected) return visibleOffers;
    return [
      initiallySelected,
      ...visibleOffers.filter((offer) => offer.offerId !== initiallySelected.offerId),
    ];
  }, [payload.offer.offerId, visibleOffers]);
  const summary = useMemo(() => summarizeOfferList(visibleOffers), [visibleOffers]);

  if (contextReleaseId) {
    const selection = selectReleaseOffers(offers, contextReleaseId, activeFormat);
    return (
      <div className="space-y-6 py-4 sm:py-5">
        <ReleaseOfferExplorerSection
          heading={t("thisEditionOffers")}
          offers={selection.releaseOffers}
          cheapestOfferId={selection.cheapestOfferId}
          contextReleaseId={contextReleaseId}
          releases={releases}
          currency={currency}
        />
        {selection.otherOffers.length > 0 && (
          <ReleaseOfferExplorerSection
            heading={t("otherEditionOffers")}
            offers={selection.otherOffers}
            cheapestOfferId={selection.cheapestOfferId}
            contextReleaseId={contextReleaseId}
            releases={releases}
            currency={currency}
          />
        )}
        <p className="px-4 text-xs text-muted-foreground sm:px-5">
          {t("catalogOffersStoreDisclosure")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 py-4 sm:space-y-4 sm:py-5">
      <div className="px-4 sm:px-5">
        <DealOfferFormatTabs
          variant="segmented"
          showSingleOption
          activeFormat={activeFormat}
          options={formatOptions}
          onSelect={setActiveFormat}
        />
      </div>
      <section key={activeFormat} className="@container min-w-0">
        <OfferListHeader summary={summary} condition={payload.condition} />
        <ul>
          {orderedOffers.map((offer) => {
            const siblings = siblingReleases(offer, releases, contextReleaseId);
            return (
              <ExplorerOfferRow
                key={offer.offerId}
                releases={releases}
                offer={offer}
                currency={currency}
                cheapest={summary.count > 1 && offer.offerId === summary.cheapestOfferId}
                contextReleaseId={contextReleaseId}
                editionContext={
                  siblings.length > 0 ? <DealOfferEditionContext releases={siblings} /> : undefined
                }
              />
            );
          })}
        </ul>
        <p className="border-t border-border/60 px-4 py-3 text-xs leading-normal text-muted-foreground sm:px-5">
          {t("catalogOffersStoreDisclosure")}
        </p>
      </section>
    </div>
  );
}
